import { Injectable, Signal } from "@angular/core";
import { ContentPipe } from "src/app/services/content/content.pipe";
import { DropdownOption } from "src/app/shared/mine-dropdown/mine-dropdown.interface";
import { AssessmentQuestionTypeEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentQuestion, AssessmentSection, AssessmentsMineOSFormControl, AssessmentsFormQuestionOption, AssessmentsQuestionOptionsFilter } from "../models/assessments.interface";

@Injectable({
  providedIn: 'root'
})
export class AiAssessmentsQuestionTypeHelper {

  private _commonOptions: AssessmentsFormQuestionOption[];
  private _specialOptions: AssessmentsFormQuestionOption[];

  constructor(
    private contentPipe: ContentPipe,
  ) {}

  private get commonOptions(): AssessmentsFormQuestionOption[] {
    this.ensureOptionsLoaded();
    return this._commonOptions;
  }

  private get specialOptions(): AssessmentsFormQuestionOption[] {
    this.ensureOptionsLoaded();
    return this._specialOptions;
  }

  /**
   * Resolve the CMS-backed question options lazily on first use — never in the constructor. This
   * root helper can be constructed during app bootstrap (via the AssessmentsService graph) before
   * the `ai-assessments` content is loaded; reading content then throws. Guarded so a not-yet-loaded
   * schema simply retries on the next access. See [[reference_contentpipe_construction_crash]].
   */
  private ensureOptionsLoaded(): void {
    if (this._commonOptions && this._specialOptions) {
      return;
    }

    const common = this.contentPipe.transform('ai-assessments.addFormQuestion.common.options') as AssessmentsFormQuestionOption[];
    const special = this.contentPipe.transform('ai-assessments.addFormQuestion.special.options') as AssessmentsFormQuestionOption[];
    if (!common || !special) {
      return; // ai-assessments content not loaded yet — retry on next access
    }

    let specialOptions = special.concat(
      this.contentPipe.transform('ai-assessments.addFormQuestionSpecialOptionsPA') as AssessmentsFormQuestionOption[]
    );
    specialOptions = specialOptions.concat(
      this.contentPipe.transform('ai-assessments.addFormQuestionDataFlow') as AssessmentsFormQuestionOption
    );

    specialOptions = specialOptions.concat(this.contentPipe.transform('ai-assessments.addFormQuestionDataTypes') as AssessmentsFormQuestionOption);

    let commonOptions = common.concat(
      this.contentPipe.transform('ai-assessments.addFormQuestionFileUpload') as AssessmentsFormQuestionOption[]
    );
    commonOptions = commonOptions.concat(
      this.contentPipe.transform('ai-assessments.addFormQuestionTextBlock') as AssessmentsFormQuestionOption[]
    );

    this._commonOptions = commonOptions;
    this._specialOptions = specialOptions;
  }

  get questionTypeOptions(): DropdownOption[] {
    const allOptions = [...this.commonOptions, ...this.specialOptions];

    const textOption = allOptions.find(option => this.getQuestionTypeEnum(option.type) === AssessmentQuestionTypeEnum.ShortAnswer);
    if (textOption) {
      allOptions.push({
        type: AssessmentQuestionTypeEnum.Text,
        label: textOption.label,
        icon: textOption.icon
      });
    }
    
    return allOptions.map(option => ({
      id: this.getQuestionTypeEnum(option.type),
      value: option.label,
      icon: option.icon
    }));
  }

  get questionTypeOptionsMap(): Map<AssessmentQuestionTypeEnum, DropdownOption> {
    const allOptions = [...this.commonOptions, ...this.specialOptions];
    const map = new Map<AssessmentQuestionTypeEnum, DropdownOption>();

    const textOption = allOptions.find(option => this.getQuestionTypeEnum(option.type) === AssessmentQuestionTypeEnum.ShortAnswer);
    if (textOption) {
      allOptions.push({
        type: AssessmentQuestionTypeEnum.Text,
        label: textOption.label,
        icon: textOption.icon
      });
      allOptions.push({
        type: AssessmentQuestionTypeEnum.TextCustomField,
        label: textOption.label,
        icon: textOption.icon
      });
    }

    const dropdownOption = allOptions.find(option => this.getQuestionTypeEnum(option.type) === AssessmentQuestionTypeEnum.Dropdown);
    if (dropdownOption) {
      allOptions.push({
        type: AssessmentQuestionTypeEnum.DropdownCustomField,
        label: dropdownOption.label,
        icon: dropdownOption.icon
      });
    }

    allOptions.forEach(option => {
      const type = this.getQuestionTypeEnum(option.type);
      map.set(type, {
        id: type,
        value: option.label,
        icon: option.icon
      });
    });

    return map;
  }

  private countQuestionsOfType(
    sections: AssessmentSection[] | AssessmentsMineOSFormControl[], 
    type: AssessmentQuestionTypeEnum
  ): number {
    const count = sections.reduce(
      (totalCount, section) => {
        const controls = this.getSectionQuestions(section) as AssessmentQuestion[];
        const sectionCount = controls.reduce((innerCount, { label: controlName, type: controlType, isDeleted }) => {
          // Skip deleted questions
          if (isDeleted) {
            return innerCount;
          }
          if (![controlType, controlName].includes(type)) {
            return innerCount;
          }
          return innerCount + 1;
        }, 0);
        return totalCount + sectionCount;
      }, 0);
    return count;
  }

  getSectionQuestions(section: AssessmentSection | AssessmentsMineOSFormControl): AssessmentQuestion[] | AssessmentsMineOSFormControl[] {
    try {
        // Check if section is null or undefined
        if (!section) {
            return [];
        }
        
        if (section.hasOwnProperty('controls')) {
            const templateSection = section as AssessmentsMineOSFormControl;
            return templateSection.controls;
        }
        else if (section['questions']) {
            return (section as AssessmentSection).questions;
        }
        return [];
    }
    catch (error) {
        console.error(error);
        return [];
    }
  }

  buildMaxQuestionsOfTypeFilter(
    sections: Signal<AssessmentSection[] | AssessmentsMineOSFormControl[]>, 
    type: AssessmentQuestionTypeEnum,
    maxAmount: number,
  ): AssessmentsQuestionOptionsFilter {
    return (options: AssessmentsFormQuestionOption[]) => {
      const count = this.countQuestionsOfType(sections(), type);
      if (count < maxAmount) {
        return options;
      }
      const filtered = options.filter(({ type: optionType }) => optionType?.toLowerCase() !== type?.toLowerCase());
      return filtered;
    };
  }

  getQuestionTypeEnum(type: string): AssessmentQuestionTypeEnum {
    return Object.values(AssessmentQuestionTypeEnum)
      .find(t => t.toLowerCase() === type?.toLowerCase()?.replace(/[^a-zA-Z]/g, "")) as AssessmentQuestionTypeEnum;
  }
}

